import React, { useEffect, useRef, useState } from "react";
import { Camera, ChevronLeft, ChevronRight, FileUp, Loader2, RefreshCw, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { compress, uploadImage } from "@/components/PhotoUpload";
import { loadScan, saveScan, clearScan, sha256, movePage, isDuplicatePage, readyFileIds } from "@/lib/scanSession";

const toDataUrl = (blob) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
const fromDataUrl = async (u, name) => new File([await (await fetch(u)).blob()], name, { type: "image/jpeg" });

// Reusable multi-page document scanner (delivery notes, invoices, price lists, other documents).
// Each page opens the device camera (native iOS/Android camera via the WebView file picker); pages are
// kept in order with thumbnails, can be retaken, deleted and reordered, and survive interruptions.
export default function DocumentScanner({ sessionKey, maxPages = 10, onComplete, testid = "doc-scanner" }) {
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const retakeIdx = useRef(-1);

  useEffect(() => { loadScan(sessionKey).then((p) => { if (Array.isArray(p) && p.length) { setPages(p); toast.info(`Restored ${p.length} scanned page(s)`); } }); }, [sessionKey]);
  const commit = (next) => { setPages(next); saveScan(sessionKey, next); };

  const uploadPage = async (page) => {
    try { const { file_id } = await uploadImage(await fromDataUrl(page.dataUrl, `page-${page.hash.slice(0, 8)}.jpg`)); return { ...page, fileId: file_id, failed: false }; }
    catch { return { ...page, fileId: null, failed: true }; }
  };

  const onPicked = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    let next = pages.slice();
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error("Scanner pages must be photos. Upload PDFs with the document upload instead."); continue; }
      // Dense invoices: keep enough resolution for small print (the 1400px photo default blurred it).
      const small = await compress(f, { max: 2400, quality: 0.85 });
      const hash = await sha256(small);
      const at = retakeIdx.current;
      if (isDuplicatePage(next, hash, at)) { toast.warning("That page is already in this scan — skipped"); continue; }
      if (at < 0 && next.length >= maxPages) { toast.error(`Maximum ${maxPages} pages`); break; }
      const page = await uploadPage({ hash, dataUrl: await toDataUrl(small) });
      if (page.failed) toast.error("Page saved on this device but not uploaded — tap retry");
      next = at >= 0 ? next.map((p, k) => (k === at ? page : p)) : [...next, page];
      commit(next);
    }
    retakeIdx.current = -1;
    setBusy(false);
  };

  const retryFailed = async () => { setBusy(true); const next = []; for (const p of pages) next.push(p.fileId ? p : await uploadPage(p)); commit(next); setBusy(false); };
  const ids = readyFileIds(pages);
  const finish = async () => { if (!ids) return; await onComplete(ids, pages.length); await clearScan(sessionKey); setPages([]); };

  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-3" data-testid={testid}>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPicked} data-testid={`${testid}-camera-input`} />
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPicked} data-testid={`${testid}-file-input`} />
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700" data-testid={`${testid}-count`}>{pages.length} page{pages.length === 1 ? "" : "s"} scanned</div>
        {busy && <Loader2 size={16} className="animate-spin text-slate-400" />}
      </div>
      {pages.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`${testid}-thumbs`}>
          {pages.map((p, i) => (
            <div key={p.hash} className="shrink-0 w-24" data-testid={`${testid}-page-${i}`}>
              <div className="relative">
                <img src={p.dataUrl} alt={`Page ${i + 1}`} className={`h-32 w-24 object-cover rounded-lg border ${p.failed ? "border-red-400" : "border-slate-200"}`} />
                <span className="absolute top-1 left-1 text-[10px] font-bold bg-white/90 rounded px-1">{i + 1}</span>
                {p.failed && <AlertTriangle size={14} className="absolute top-1 right-1 text-red-500" />}
              </div>
              <div className="flex justify-between mt-1 text-slate-500">
                <button type="button" aria-label="Move left" disabled={i === 0} onClick={() => commit(movePage(pages, i, -1))} data-testid={`${testid}-left-${i}`}><ChevronLeft size={16} /></button>
                <button type="button" aria-label="Retake" onClick={() => { retakeIdx.current = i; camRef.current?.click(); }} data-testid={`${testid}-retake-${i}`}><RefreshCw size={14} /></button>
                <button type="button" aria-label="Delete" onClick={() => commit(pages.filter((_, k) => k !== i))} data-testid={`${testid}-delete-${i}`}><Trash2 size={14} /></button>
                <button type="button" aria-label="Move right" disabled={i === pages.length - 1} onClick={() => commit(movePage(pages, i, 1))} data-testid={`${testid}-right-${i}`}><ChevronRight size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" size="sm" disabled={busy || pages.length >= maxPages} onClick={() => { retakeIdx.current = -1; camRef.current?.click(); }} data-testid={`${testid}-add-page`}><Camera size={15} className="mr-1" /> {pages.length ? "Scan next page" : "Scan first page"}</Button>
        <Button type="button" variant="outline" size="sm" disabled={busy || pages.length >= maxPages} onClick={() => { retakeIdx.current = -1; fileRef.current?.click(); }} data-testid={`${testid}-add-photos`}><FileUp size={15} className="mr-1" /> Add photos</Button>
      </div>
      {pages.some((p) => p.failed) && <Button type="button" size="sm" variant="outline" className="w-full" onClick={retryFailed} disabled={busy} data-testid={`${testid}-retry`}>Retry failed uploads</Button>}
      {pages.length > 0 && <Button type="button" size="sm" className="w-full bg-brand hover:bg-brand-hover" disabled={busy || !ids} onClick={finish} data-testid={`${testid}-done`}>Use {pages.length} page{pages.length === 1 ? "" : "s"} as one document</Button>}
    </div>
  );
}
